

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TableView } from "../types/TableView";
import { viewChanged } from "@/lib/utils/changes-spotter";
import { defaultView } from "@/lib/utils/default-returner";
import {
  ROLE_DEFAULT_SURFACES,
  type RoleDefaultSurface,
} from "@/types/RoleSurfaceDefault";
import { removePref, setPref, setJSONPref } from "@/lib/storage/userPrefs";
import {
  clearViewCache,
  plainListKey,
  tableCacheKey,
  type AdhocSurface,
} from "@/lib/storage/adhocTableState";

// Define the context
type ViewContextType = {
  viewsRef: React.RefObject<TableView[]>;
  hasMoreRef: React.RefObject<boolean>;
  activeViewState: TableView;
  setActiveViewState: React.Dispatch<React.SetStateAction<TableView>>;
  activateView: (newView: TableView) => void;
  deleteViewCookie: () => void;
  /**
   * The provider's `persist` prop, exposed for consumers that must know
   * whether they sit on the canonical list surface (`true`) or on a
   * popup-scoped mount such as the send-email recipient picker (`false`):
   * the JSON view transfer renders its buttons only on the former.
   */
  persist: boolean;
};

const ViewContext = createContext<ViewContextType | undefined>(undefined);

// Provider component
export function ViewsProvider({
  activeView,
  activeViewSaved,
  views,
  eventId,
  children,
  type,
  persist = true,
}: {
  activeView: TableView;
  /**
   * The SERVER's copy of `activeView`, when the caller handed in a locally
   * restored one (see `applyAdhocView`). `viewsRef` is the comparison base for
   * "fields changed" and for deciding whether an applied view still matches
   * what is stored — so it must hold server truth, never the user's local
   * variant, or a restored view would compare equal to itself and both the
   * indicator and the snapshot logic would go silent. Defaults to `activeView`
   * for callers that restore nothing.
   */
  activeViewSaved?: TableView;
  views?: TableView[];
  eventId?: string;
  children: React.ReactNode;
  /**
   * When false, this provider performs NO userPrefs IO — no snapshot on
   * activate, no DEFAULT-cleanup removal on mount, no cookie delete. Mirror of
   * the flag `FiltersProvider` already carries, and for the same reason: a
   * popup-scoped provider mounted under a LIST's pref keys (the standalone
   * guest detail, the send-email dialog, the embedded CRM pane) otherwise
   * erases that list's saved view the moment it opens with a DEFAULT seed.
   */
  persist?: boolean;
  type:
    | "event"
    | "guest"
    | "registrant"
    | "companion"
    | "emailSent"
    | "eventLimits"
    | "checkIn"
    | "actionLog"
    // CRM contact pool — `eventId` carries the company id on this surface.
    | "contact"
    // Wave A — speaker/sponsor hub DataTables (event-scoped).
    | "speaker"
    | "sponsor";
}) {
  const viewsRef = useRef<TableView[]>([activeViewSaved ?? activeView, ...(views ?? [])]);
  const [activeViewState, setActiveViewState] = useState<TableView>(activeView);
  const hasMoreRef = useRef<boolean>((views?.length ?? 0) >= 11);

  // A persisting mount on a NAMED view without its server copy is the failure
  // that shipped once already: `viewsRef` then holds the restored variant, every
  // "has this changed?" question compares it with itself, and the cache deletes
  // itself on the next apply. tsc cannot see it (the prop is optional) and the
  // suite could not either, so say it out loud where a developer meets it — once
  // per mount, in an effect, so a StrictMode double render does not double it.
  useEffect(() => {
    if (
      import.meta.env.DEV &&
      persist &&
      // `eventLimits` bails out of every write before touching a pref, and its
      // seed is a literal with no id at all — it would trip this on every visit
      // to the subscription page while having nothing to compare in the first
      // place.
      type !== "eventLimits" &&
      // `== null`, because `viewsRef` seeds with `??` — an explicitly passed
      // `null` (the requests page carries `TableView | null` lanes) is the very
      // defect described below, and `=== undefined` would let it pass in
      // silence.
      activeViewSaved == null &&
      activeView?.tableViewId !== "DEFAULT"
    )
      console.error(
        `ViewsProvider(type="${type}"): a persisting mount on the named view ` +
          `"${activeView?.name}" got no activeViewSaved — pass the SERVER copy, ` +
          `or the view-cache comparisons silently compare a thing with itself.`,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine cookie name based on type and eventId
  const getCookieName = useCallback(() => {
    if (type === "event") {
      return `view-event`;
    } else {
      return `view-${type}-${eventId}`;
    }
  }, [type, eventId]);

  useEffect(() => {
    if (type === "eventLimits" || !persist) return;
    if (activeViewState?.name === "DEFAULT") {
      removePref(getCookieName());
    }
  }, []);

  const activateView = useCallback(
    (newView: TableView) => {
      if (type === "eventLimits") return;

      if (viewChanged(activeViewState, newView)) {
        setActiveViewState(newView);
        const selectedIndex = viewsRef.current.findIndex(
          (view) => view.tableViewId === newView.tableViewId,
        );

        if (selectedIndex > 0) {
          const [selectedView] = viewsRef.current.splice(selectedIndex, 1);
          viewsRef.current.unshift(selectedView);
        }
      }
      if (!persist) return;

      if (newView.tableViewId !== "DEFAULT") {
        setPref(getCookieName(), newView.tableViewId!);
      } else {
        // An unnamed setup has no id to select next time.
        removePref(getCookieName());
      }

      // Remember what was APPLIED whenever it differs from what the server
      // holds — for the unnamed DEFAULT (which has nowhere else to live) and
      // for a named view the user tweaked without saving. The entry carries the
      // view's id AND the server's column list at the time, so `applyAdhocView`
      // can only restore it onto that same view while that same base still
      // stands.
      const surface = type as AdhocSurface;
      const isDefault = newView.tableViewId === "DEFAULT";
      const serverCopy =
        viewsRef.current.find(
          (view) => view.tableViewId === newView.tableViewId,
        ) ??
        // The plain list needs no server copy to be known: it IS the constant.
        // Pages do seed it into `views` whenever it is not the active view, but
        // resting the "user chose the plain list" record on that would make the
        // record disappear the day a page stops doing it — and the record is
        // the only thing standing between this user and the role's default view
        // coming back on every load.
        (isDefault ? defaultView(type) : undefined);
      // `undefined` is NOT "no columns" — it is "this view is not in the list I
      // hold" (a search hit, or a view from a later page). Treating it as an
      // empty column list made every such apply look modified and cached a view
      // the user never touched, freezing it against later server edits. When we
      // cannot tell, we do not remember.
      if (!serverCopy) return;
      const modified =
        JSON.stringify(serverCopy.fields ?? []) !==
        JSON.stringify(newView.fields ?? []);

      if (modified && (newView.fields?.length ?? 0) > 0) {
        setJSONPref(tableCacheKey("view", surface, String(eventId)), {
          baseViewId: newView.tableViewId ?? "DEFAULT",
          baseFields: serverCopy.fields ?? [],
          fields: newView.fields,
        });
      } else {
        // Only THIS view's memory. The slot is shared by the whole surface, so
        // an unconditional delete here threw away the columns of whichever
        // OTHER view happened to be remembered — switching from a tweaked A to
        // an untouched B silently lost A's work.
        clearViewCache(surface, String(eventId), newView.tableViewId);
      }

      // The choice of the plain list is a PREFERENCE, kept apart from the
      // column cache (see `plainListKey`): it must survive a cache that is
      // cleared, and it must not cost another view its remembered columns.
      //
      // It records the LAST thing that was applied, so a named view clears it
      // again. Leaning on "the stored id wins anyway" was not enough: the id
      // goes away on three paths (activating DEFAULT, `deleteViewCookie`, the
      // mount cleanup), and a trace left over from months ago would then
      // suppress the role's default view for good, with no way back through the
      // UI.
      //
      // Only where a role default can exist — `ROLE_DEFAULT_SURFACES`. On the
      // other seven surfaces nobody ever reads this key, and since it is a
      // preference the cache reset does not collect it: it would be one dead
      // entry per surface and event, forever.
      if (ROLE_DEFAULT_SURFACES.includes(type as RoleDefaultSurface)) {
        const traceKey = plainListKey(surface, String(eventId));
        if (isDefault) setPref(traceKey, "1");
        else removePref(traceKey);
      }
    },
    [activeViewState, getCookieName, persist, type, eventId],
  );

  const deleteViewCookie = useCallback(() => {
    if (type === "eventLimits" || !persist) return;
    removePref(getCookieName());
  }, [getCookieName, persist, type]);

  const contextValue = useMemo(() => ({
    hasMoreRef, viewsRef, activeViewState, setActiveViewState, activateView, deleteViewCookie, persist,
  }), [activeViewState, activateView, deleteViewCookie, persist]);

  return (
    <ViewContext.Provider value={contextValue}>
      {children}
    </ViewContext.Provider>
  );
}

// Hook for consuming the context
export function useViews() {
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error("useView must be used within a ViewProvider");
  }
  return context;
}

/**
 * Non-throwing variant for consumers that may mount outside a ViewsProvider
 * (e.g. the guest-detail layout resolution hook, which the layout BUILDER
 * page renders with no view context at all). Returns undefined there.
 */
export function useViewsOptional() {
  return useContext(ViewContext);
}
